import React, { useState, useEffect } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import {
  Container, Typography, Paper, Box, Button, Chip, Alert, CircularProgress,
  Table, TableBody, TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUS = { active: 'Активен', completed: 'Завершён', draft: 'Черновик', pending_moderation: 'На модерации', rejected: 'Отклонён' };

// Ответ в читаемом виде (массивы, матрицы и объекты — «строка: значение»)
function formatAnswer(v) {
  if (v === null || v === undefined || v === '') return '—';
  if (Array.isArray(v)) return v.map(formatAnswer).join(', ');
  if (typeof v === 'object') {
    return Object.entries(v).map(([k, val]) => `${k}: ${formatAnswer(val)}`).join('; ');
  }
  return String(v);
}

// Защита от формульной инъекции в Excel (ведущие = + - @ и управляющие)
const escapeFormula = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
};

// Уникальное имя колонки (одинаковые тексты вопросов не затирают друг друга)
const uniqueHeader = (used, base) => {
  const name = String(base || 'Вопрос').slice(0, 200) || 'Вопрос';
  let candidate = name;
  let n = 2;
  while (used.has(candidate)) { candidate = `${name} (${n})`; n += 1; }
  used.add(candidate);
  return candidate;
};

// Excel интервьюера: только его ответы, основные и дополнительные вопросы вместе
export function buildInterviewerSheetRows(details) {
  const used = new Set(['№', 'Дата', 'Время (сек)', 'Статус']);
  const main = (details.questions || []).map((q) => ({ id: q.id, header: uniqueHeader(used, q.text), extra: false }));
  const extra = (details.interviewerQuestions || []).map((q) => ({
    id: q.id, header: uniqueHeader(used, q.archived ? `${q.text} (архив)` : q.text), extra: true
  }));
  const total = (details.responses || []).length;
  return (details.responses || []).map((rw, idx) => {
    const row = {
      '№': total - idx,
      'Дата': rw.completedAt ? new Date(rw.completedAt).toLocaleString() : '',
      'Время (сек)': rw.timeSpent || '',
      'Статус': rw.status || ''
    };
    const cell = (v) => { const f = formatAnswer(v); return escapeFormula(f === '—' ? '' : f); };
    main.forEach((c) => { row[c.header] = cell((rw.answers || {})[c.id]); });
    extra.forEach((c) => { row[c.header] = cell((rw.interviewerAnswers || {})[c.id]); });
    return row;
  });
}

// Интервьюер: назначенные опросы (только чтение): своя ссылка, счётчик и свои ответы
export default function AssignedSurveys() {
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [details, setDetails] = useState(null);

  const questionText = (qid) => {
    const q = (details?.questions || []).find((x) => String(x.id) === String(qid));
    return (q && q.text) || 'Вопрос';
  };

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_BASE}/surveys/assigned`, { headers: headers() });
        setSurveys(Array.isArray(data.surveys) ? data.surveys : []);
      } catch (e) {
        setError(e.response?.data?.error || 'Не удалось загрузить назначенные опросы');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const openDetails = async (survey) => {
    try {
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/interviewer-stats`, { headers: headers() });
      setDetails(data);
    } catch (e) {
      setError(e.response?.data?.error || 'Не удалось загрузить ответы');
    }
  };

  const exportExcel = () => {
    if (!details) return;
    const ws = XLSX.utils.json_to_sheet(buildInterviewerSheetRows(details));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Мои ответы');
    XLSX.writeFile(wb, `my_responses_${details.survey?.id || 'survey'}_${Date.now()}.xlsx`);
  };

  const iqText = (qid) => {
    const q = (details?.interviewerQuestions || []).find((x) => x.id === qid);
    return (q && q.text) || 'Доп. вопрос';
  };

  const copy = (link) => {
    navigator.clipboard.writeText(link);
    setInfo('Ссылка скопирована');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Назначенные опросы</Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}
      {loading ? <CircularProgress /> : surveys.length === 0 ? (
        <Typography color="text.secondary">Вам пока не назначены опросы</Typography>
      ) : (
        <Paper sx={{ p: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Опрос</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Мои ответы</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {surveys.map((s) => (
                <TableRow key={s.id}>
                  <TableCell>{s.title}</TableCell>
                  <TableCell><Chip size="small" label={STATUS[s.status] || s.status} /></TableCell>
                  <TableCell align="right">{s.responseCount}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                      {s.link && <Button size="small" variant="outlined" onClick={() => copy(s.link)}>Скопировать ссылку</Button>}
                      <Button size="small" variant="contained" onClick={() => openDetails(s)}>Мои ответы</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Dialog open={!!details} onClose={() => setDetails(null)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Мои ответы: {details?.survey?.title}</DialogTitle>
        <DialogContent dividers>
          {details && (
            <>
              <Typography sx={{ mb: 1 }}>Собрано ответов: {details.responseCount}</Typography>
              {details.link && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2, wordBreak: 'break-all' }}>
                  Моя ссылка: {details.link}
                </Typography>
              )}
              {details.responses.length === 0 ? (
                <Typography color="text.secondary">Пока нет ответов</Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>№</TableCell>
                      <TableCell>Время</TableCell>
                      <TableCell>Ответы</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {details.responses.map((rw, idx) => (
                      <TableRow key={rw.id}>
                        <TableCell>{details.responses.length - idx}</TableCell>
                        <TableCell>{rw.completedAt ? new Date(rw.completedAt).toLocaleString() : '-'}</TableCell>
                        <TableCell sx={{ maxWidth: 480, wordBreak: 'break-word' }}>
                          {Object.entries(rw.answers || {}).map(([qid, v]) => (
                            <Box key={qid} sx={{ mb: 0.5 }}>
                              <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                                {questionText(qid)}:
                              </Typography>{' '}
                              <Typography component="span" variant="body2">{formatAnswer(v)}</Typography>
                            </Box>
                          ))}
                          {Object.entries(rw.interviewerAnswers || {}).map(([qid, v]) => (
                            <Box key={qid} sx={{ mb: 0.5 }}>
                              <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                                {iqText(qid)}:
                              </Typography>{' '}
                              <Typography component="span" variant="body2">{formatAnswer(v)}</Typography>
                            </Box>
                          ))}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={exportExcel} disabled={!details || details.responses.length === 0}>Скачать Excel</Button>
          <Button onClick={() => setDetails(null)}>Закрыть</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
